import React, { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faArrowCircleDown, faSearch } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, InputText } from "@performyard/py-components";

import { AIButton } from "../../../../../../../components/AIModules/AIButton/AIButton";
import ScrollableTable from "../../../../../../../components/TableLite/TableLiteScrollable";

import { useDownloadSurveyResultsCSV, useGetSurveyQuestionsResults } from "../../../../../../../api/hooks";
import { useSurvey } from "../../../hooks/useSurvey";
import { getSurveyResultsColumns } from "../hooks/getSurveyResultsColumns";
import { useSurveyResultsKPIs } from "../hooks/useSurveyResultsKPIs";

import EmptySearchTableMessage from "../../../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";

export function SurveyResultsTable({ surveyId }: { surveyId: string }) {
	const { survey } = useSurvey({ surveyId });
	const { mutate: downloadCSV } = useDownloadSurveyResultsCSV(surveyId);
	const { participantSubmissions } = useSurveyResultsKPIs({ surveyId });

	const [searchValue, setSearchValue] = useState("");
	const [activeSort, setActiveSort] = useState<{
		field: string;
		direction: "asc" | "desc";
	} | null>(null);
	const [debouncedSearchValue] = useDebounceValue(searchValue, 500);

	const {
		data: surveyQuestionsResults,
		refetch,
		isLoading,
	} = useGetSurveyQuestionsResults(surveyId, {
		...(debouncedSearchValue.length
			? {
					search: {
						field: "questions.text",
						operator: "re",
						value: debouncedSearchValue,
					},
				}
			: {}),
		...(activeSort
			? {
					sorts: {
						[activeSort.field]: activeSort.direction === "asc" ? 1 : -1,
					},
				}
			: {}),
	});

	useEffect(() => {
		refetch();
	}, [debouncedSearchValue]);

	const handleColumnClick = (column: string) => {
		const sameColumn = column === activeSort?.field;
		if (sameColumn && activeSort?.direction === "desc") {
			setActiveSort(null);
		} else {
			setActiveSort({
				field: column,
				direction: sameColumn ? "desc" : "asc",
			});
		}
		refetch();
	};

	const handleDownloadCSV = () => {
		downloadCSV({});
	};

	const columns = getSurveyResultsColumns();

	return (
		<div className="flex flex-col gap-4" data-testid="survey-results-table">
			<div className="flex flex-row items-center justify-between">
				<div className="w-[288px]">
					<InputText
						onChange={setSearchValue}
						placeholder="Search Questions..."
						leftIcon={faSearch}
						value={searchValue}
					/>
				</div>
				<div className="flex flex-row items-center gap-2">
					{["active", "closed"].includes(survey?.status ?? "") && participantSubmissions.value > 0 && (
						<AIButton buttonSize="medium" moduleType="surveyInsights" surveyId={surveyId} />
					)}
					<span className="w-fit">
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="medium"
							iconLeft={faArrowCircleDown}
							onClick={handleDownloadCSV}
						>
							Download CSV
						</ButtonStandard>
					</span>
				</div>
			</div>
			<ScrollableTable
				columns={columns}
				rows={surveyQuestionsResults?.questions || []}
				isLoading={isLoading}
				headCellClass="h-9 flex items-center"
				rowClassName="h-8 flex items-center"
				emptyChildren={<EmptySearchTableMessage mainText="No Questions Found" />}
				onColumnClick={handleColumnClick}
				activeSort={activeSort?.field}
				activeSortDirection={activeSort?.direction}
			/>
		</div>
	);
}
